import React, { Fragment } from "react";
import { RouteComponentProps } from "react-router";
import { Row, Col, Table, message } from "antd";
import Card from "../components/Card";
import { DownloadableTemplate } from "../helpers/shared-interfaces";
import {
  formatSnakeCase,
  generateDisplayErrorMessage,
} from "../helpers/utilities";
import { PageToolbar } from "../components/PageToolbar";
import { DownloadOutlined } from "@ant-design/icons";
import axios from "axios";
import * as Sentry from "@sentry/browser";
import { TEMPLATE_API_ENDPOINT } from "../helpers/constants";

interface State {
  isLoading: boolean;
  templates: DownloadableTemplate[];
  search: string;
}

export const columns = [
  {
    title: "Template Name",
    dataIndex: "name",
    key: "name",
    width: "65%",
    render(text: string): JSX.Element {
      return <span style={{ textTransform: "capitalize" }}>{text}</span>;
    },
  },
  {
    title: "Action",
    dataIndex: "url",
    key: "url",
    align: "center" as const,
    render(text: string): JSX.Element {
      return (
        <a href={text} download>
          <DownloadOutlined />
        </a>
      );
    },
  },
];

export function TemplatesTableFactory(
  data: DownloadableTemplate[],
  isLoading: boolean,
): JSX.Element {
  const tempData: DownloadableTemplate[] = data.map((template) => {
    return { ...template, key: template.name };
  });
  return (
    <Table
      loading={isLoading}
      style={{ width: "100%" }}
      columns={columns}
      dataSource={tempData}
      pagination={{ pageSize: 10 }}
    />
  );
}

export default class DownloadableTemplates extends React.Component<
  RouteComponentProps,
  State
> {
  constructor(props: Readonly<RouteComponentProps>) {
    super(props);
    this.state = {
      isLoading: true,
      templates: [],
      search: "",
    };
  }

  async componentDidMount(): Promise<void> {
    this.setState({ isLoading: true });
    await this.getTemplates();
  }

  getTemplates = async (): Promise<void> => {
    try {
      const response = await axios.get(TEMPLATE_API_ENDPOINT);
      const data: Record<string, string> = response.data;
      const formattedData: DownloadableTemplate[] = Object.keys(data).map(
        (template) => {
          return {
            id: template,
            name: formatSnakeCase(template),
            url: data[template],
          };
        },
      );
      this.setState({
        templates: formattedData,
        isLoading: false,
      });
    } catch (error) {
      message.error(
        `Failed to load templates - ${generateDisplayErrorMessage(error)}`,
      );
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ search: e.target.value });
  };

  render(): JSX.Element | null {
    const { templates, isLoading, search } = this.state;
    const filteredTemplates = templates.filter((t) =>
      t.name.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <Card>
          <Fragment>
            <PageToolbar modelName="templates"
              searchPlaceholder="Search by template name"
              searchValue={search}
              onSearchChange={this.handleSearch}
              totalCount={templates.length}
              currentDataCount={filteredTemplates.length}
              page={1}
              pageSize={10}
            />
            <Row style={{ marginBottom: "18px" }}>
              <Col span={24}>
                {TemplatesTableFactory(filteredTemplates, isLoading)}
              </Col>
            </Row>
          </Fragment>
        </Card>
    );
  }
}
